import { type Dispatch, type FC, type SetStateAction, useCallback, useState } from "react";
import { axiosPost } from "@root/shared/utils/axiosForRails";
import type {
  PhotoContestEntry,
  VoteButtonText,
  RemainingVotableCountsByDivision,
} from "@clientFrontend/features/campaign/components/UserPhotoContest/type";
import { type AxiosError, type AxiosResponse } from "axios";
import { reloadPage, showAlert } from "@root/shared/utils";
import { Presentation } from "./Presentation";

declare const newrelic: { noticeError: (error: Error) => void };

type ContestEntryVoteResponse = {
  clientRemainingVotableCounts: RemainingVotableCountsByDivision;
};

type Props = {
  entryNo: number;
  photoContestEntry: PhotoContestEntry;
  isClientScoutable: boolean;
  votePath: string;
  commentPath: string;
  isClientNotVotable: boolean;
  isVotingPeriodOver: boolean;
  setClienRemainingVotableCounts: Dispatch<SetStateAction<RemainingVotableCountsByDivision>>;
};

export const ContestEntryCassette: FC<Props> = ({
  entryNo,
  photoContestEntry,
  isClientScoutable,
  votePath,
  commentPath,
  isClientNotVotable,
  isVotingPeriodOver,
  setClienRemainingVotableCounts,
}) => {
  const [isAccordionOpened, setIsAccordionOpened] = useState<boolean>(false);
  const [isEntryVoted, setIsEntryVoted] = useState<boolean>(photoContestEntry.isEntryVoted);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [isCommentSubmitting, setIsCommentSubmitting] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  const handleVoteContestEntry = useCallback(async () => {
    setIsEntryVoted(true);
    await axiosPost
      .post(votePath, {
        user_photo_contest_entry_id: photoContestEntry.id,
      })
      .then((response: AxiosResponse<ContestEntryVoteResponse>) => {
        setClienRemainingVotableCounts(response.data.clientRemainingVotableCounts);
        setIsCommentModalOpen(true);
      })
      .catch((error: AxiosError) => {
        if (typeof newrelic !== "undefined") {
          newrelic.noticeError(error);
        }
        if (window.confirm("エラーが発生しました。ページをリロードして再度試しますか？")) {
          reloadPage();
        }
      });
  }, [
    setIsEntryVoted,
    setClienRemainingVotableCounts,
    votePath,
    photoContestEntry.id,
    setIsCommentModalOpen,
  ]);

  const handleSubmitComment = useCallback(async () => {
    if (!comment.trim()) {
      setIsCommentModalOpen(false);
      setComment("");
      return;
    }

    setIsCommentSubmitting(true);

    await axiosPost
      .post(commentPath, {
        user_photo_contest_entry_id: photoContestEntry.id,
        comment: comment.trim(),
      })
      .then(() => {
        // flashメッセージを表示するため、ページを再読み込みする
        window.location.href = window.location.pathname;
      })
      .catch((error: AxiosError) => {
        if (typeof newrelic !== "undefined") {
          newrelic.noticeError(error);
        }
        showAlert("送信に失敗しました。時間をおいて再度お試しください。");
        setIsCommentSubmitting(false);
      });
  }, [comment, commentPath, photoContestEntry.id]);

  const voteButtonText = useCallback<() => VoteButtonText>(() => {
    if (isClientNotVotable) return "投票数の上限に達しました";
    if (isEntryVoted) return "投票済み";
    return "投票する";
  }, [isClientNotVotable, isEntryVoted]);

  return (
    <Presentation
      entryNo={entryNo}
      isEntryVoted={isEntryVoted}
      isAccordionOpened={isAccordionOpened}
      isClientScoutable={isClientScoutable}
      photoContestEntry={photoContestEntry}
      isClientNotVotable={isClientNotVotable}
      isVotingPeriodOver={isVotingPeriodOver}
      voteButtonText={voteButtonText()}
      toggleAccordion={() => setIsAccordionOpened(!isAccordionOpened)}
      handleVoteContestEntry={handleVoteContestEntry}
      isCommentModalOpen={isCommentModalOpen}
      setIsCommentModalOpen={setIsCommentModalOpen}
      comment={comment}
      setComment={setComment}
      handleSubmitComment={handleSubmitComment}
      isCommentSubmitting={isCommentSubmitting}
    />
  );
};
